// frontend/src/pages/SocialReportTemplatePage.js
//
// /tools/social-media-report-template
//
// TARGET #8 of the ranked eight — the smallest by volume and one of only two in
// the cluster with NO AI Overview, which is why it survived the ranking at all.
//
// MEASURED 2026-08-16/17:
//   social media report            590/mo  KD 16  CPC $40.70
//   social media reporting         590/mo  KD 16  CPC $40.70
//   social media audit template    480/mo  KD  9  CPC  $8.99
//   social media report template   480/mo  KD 16  CPC  $7.50
//   social media reporting template 480/mo KD 16  CPC  $7.50
//   family total 3,160 searches, every term KD 0-17
//
// The page targets `social media report` rather than the template phrasing: same
// KD, more volume, and five times the CPC.
//
// THE WALL IS BRAND, NOT LINKS. hubspot.com/resources/templates/social-media-report
// ranks #1 with 5 referring domains, canva 4, adobe 7. They are there on domain
// authority, so the entry cost is a better page rather than a link campaign.
//
// THE DIFFERENTIATOR IS THAT IT CALCULATES. Every competing result hands you a
// blank grid to type into. This one works the derived metrics out — and does it
// with each platform's own engagement formula rather than one generic formula,
// the same correction /tools/engagement-rate-calculator makes.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import SocialReportBuilder from '../components/SocialReportBuilder'

const URL = '/tools/social-media-report-template'
const HERO = '/assets/images/articles/social-report.webp'

const FAQS = [
  ['What should a social media report include?',
   'Followers at the start and end of the period, posts published, total engagements, reach where you have it, and engagement rate. Everything else is commentary. A report that lists twenty metrics and explains none of them is harder to act on than one that shows five and says what changed.'],
  ['How do you write a social media report?',
   'Pull the numbers for the period, work out the derived metrics, then write two or three sentences saying what changed and why you think it changed. The builder above handles the first two parts; the third is the part that makes it worth reading.'],
  ['How often should you report on social media?',
   'Monthly for most teams. Weekly reporting produces noise — individual posts swing the numbers far more than anything you did strategically — and quarterly is too slow to change course.'],
  ['What is a good engagement rate to report?',
   'It depends on the platform, and the platform decides the formula too. Medians run around 2.60% on TikTok and 0.48% on Instagram. Report your own trend alongside the benchmark, because trend is the part you control.'],
  ['Should I report engagement rate by follower or by reach?',
   'Both, and label which is which. By follower is the benchmarking number; by reach is what most clients and brand partners mean. They diverge sharply on a post that travelled beyond your following, and quoting the wrong one is how numbers get disputed.'],
  ['Is this report template free?',
   'Yes — no sign-up and no email required. It runs in your browser and exports as a CSV or plain text you can paste anywhere.'],
]

const SECTIONS = [
  ['Report the trend, not the total',
   'A month with 4,000 engagements means nothing on its own. The same month against 2,800 last month means something, and against 6,100 last month means something rather urgent. Always include the prior period — the builder above asks for start and end followers for exactly this reason, and a report without a comparison is a list.'],
  ['Use each platform\'s own engagement formula',
   'Instagram counts likes and comments. TikTok also counts shares and saves. Facebook counts every reaction type. Applying one formula across all of them under-reports TikTok badly and makes cross-platform comparisons meaningless. Most report templates ignore this entirely; the builder above uses the right formula per network and says which it used.'],
  ['Say what you think happened',
   'The difference between a report that gets read and one that gets filed is two sentences of interpretation. "Engagement fell 18%, driven by three weeks with only one post each" is useful. A table with no narrative asks the reader to do your job.'],
  ['Keep the same shape every month',
   'Changing which metrics you report is how reporting quietly becomes unfalsifiable — there is always a number that went up. Fix the format, keep the same columns, and let the numbers say what they say.'],
]

export default function SocialReportTemplatePage () {
  return (
    <>
      <Seo
        title='Social Media Report Template — Free Builder'
        description='Free social media report template that does the maths — follower growth, engagement rate per platform, average per post. Export as CSV or text.'
        path={URL}
        image={HERO}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: FAQS.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: 'Social media report builder',
            url: `${SITE_URL}${URL}`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>Social media report template</h1>
              <p className='lead mb-0'>
                Enter last month's numbers and get a finished report — follower growth, engagement
                rate worked out with each platform's own formula, and a CSV you can send on.
              </p>
            </Col>
            <Col lg={5}>
              <Image src={HERO} alt='Charts and figures on a laptop screen' fluid rounded
                     loading='eager' width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <SocialReportBuilder />
        </Container>
      </section>

      {/* ───────── THE ANSWER ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            <strong>A social media report should show followers at the start and end of the
            period, posts published, total engagements, reach, and engagement rate.</strong>{' '}
            That is the whole of it. Everything beyond those is commentary, and commentary is
            what makes it worth reading — not more metrics.
          </p>
          <p className='text-muted fs-5 mb-0'>
            The part most templates leave to you is the arithmetic, and the part most get wrong
            is engagement rate, because every platform defines it differently.
          </p>
        </Container>
      </section>

      {/* ───────── WHAT'S IN IT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>What the report works out for you</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr><th>Metric</th><th>How it is calculated</th></tr>
              </thead>
              <tbody>
                {[['Follower growth', 'End followers minus start followers, and as a percentage'],
                  ['Engagement rate by follower', "Engagements ÷ followers × 100 — the benchmarking number"],
                  ['Engagement rate by reach', 'Engagements ÷ reach × 100 — what a client usually means'],
                  ['Average engagements per post', 'Total engagements ÷ posts published'],
                  ['What counts as engagement', "Stated per network, because Instagram counts likes and comments while TikTok also counts shares and saves"]].map(([m, h]) => (
                  <tr key={m}>
                    <td className='fw-semibold'>{m}</td>
                    <td className='text-muted'>{h}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            Exports as a CSV for a spreadsheet, or plain text to paste into an email or a deck.
          </p>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {SECTIONS.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly Social
                you can plan and publish to all eight networks — Instagram, TikTok, Facebook,
                LinkedIn, YouTube, Pinterest, Threads and X — from one calendar.
              </p>
              <p className='text-muted'>
                Which is the other half of reporting: most months that read badly are months where
                posting slipped, not months where the content was wrong. Plans start at $9/month
                and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {[['/tools/engagement-rate-calculator', 'Engagement rate calculator', 'The per-platform formulas'],
              ['/tools/content-calendar-template', 'Content calendar template', 'Plan the month you are about to report on'],
              ['/best-time-to-post', 'Best time to post', 'A schedule for your audience'],
              ['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools/instagram-post-size', 'Image resizer', 'Every Instagram size'],
              ['/tools/linkedin-banner-size', 'LinkedIn sizes', 'Dimensions and a resizer']].map(([href, t, d]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{t}</div>
                  <div className='text-muted small'>{d}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Most bad months are posting months, not content months</h2>
          <p className='text-muted fs-5'>
            At Greatly Social you can schedule the month before it happens, so the report is
            measuring a plan rather than whatever got posted.
          </p>
          <Row className='g-3 mb-4'>
            {[['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A month in one upload', 'Queue a whole month from a spreadsheet.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a report.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/calendar-view'>See the calendar</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can plan the month you report on</h2>
          <p className='lead mb-4'>
            Schedule across eight networks in one sitting. Free for 14 days, no card, plans from
            $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/tools/content-calendar-template' className='mb-2'>
            Plan next month
          </Button>
        </div>
      </section>
    </>
  )
}
